import type { EventPayloadMap } from '../../../shared/EventBus/EventPayloadMap';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import type { CastGeometry } from './castGeometry';

export type RemoteProjectileReleaseCandidate = EventPayloadMap['vfx:remote_projectile_release'];
export const REMOTE_RELEASE_GRACE_MS = 250;
export const REMOTE_RELEASE_TTL_MS = 5000;
export const REMOTE_RELEASE_CAP = 256;
const legacyKeys = new WeakMap<object, string>(), scopes = new WeakMap<object, string>();
let nextLegacy = 0, nextScope = 0;
const sequence = (n: unknown): n is number => typeof n === 'number' && Number.isSafeInteger(n) && n > 0;

/** An opaque client-only room identity, never serialized to the server. */
export function remoteProjectileScopeKey(): string {
  const registry = ServiceRegistry.getInstance();
  const keyFor = (owner: object | null | undefined) => {
    if (!owner) return 'absent';
    let key = scopes.get(owner);
    if (!key) { key = `scope-${++nextScope}`; scopes.set(owner, key); }
    return key;
  };
  return `${keyFor(registry.get('NetworkManager', { optional: true })?.getRoom?.())}:${keyFor(registry.get('EntitySystem', { optional: true }))}`;
}

/** Both subscribers receive the same cast object. Legacy messages therefore
 * share a weak identity without inventing a protocol sequence or mutating it. */
export function remoteProjectileCastKey(data: object, casterId: string, abilityId: string, clientCastSeq?: unknown): string {
  const scope = remoteProjectileScopeKey();
  if (sequence(clientCastSeq)) return `${scope}:${casterId}:${abilityId}:${clientCastSeq}`;
  let key = legacyKeys.get(data);
  if (!key) { key = `${scope}:legacy-${++nextLegacy}`; legacyKeys.set(data, key); }
  return key;
}

/** Only the redundant instant beat delegates its body glow. A separate
 * wind-up/cast identity is still owned by the existing cast presenter. */
export function sharesInstantProjectileRelease(geometry: CastGeometry | undefined, castEffectId: string): boolean {
  return !!geometry?.projectile?.muzzle && geometry.projectile.windUpMs === 0
    && geometry.projectile.muzzle.effectId === castEffectId;
}

export interface RemoteReleaseIdentity {
  casterId: string; abilityId: string; castKey: string; scopeKey: string; clientCastSeq?: number;
}
export interface RemoteReleaseActor { identity: object; health?: number; isDead?: boolean; isOnline?: boolean }
interface Entry extends RemoteReleaseIdentity {
  actor: object | null; dueAt: number; expiresAt: number; settled: boolean;
  fallback?: () => RemoteProjectileReleaseCandidate | null;
}
interface Dependencies {
  caster(id: string): RemoteReleaseActor | null;
  emit(candidate: RemoteProjectileReleaseCandidate): void;
  scope?: () => string;
  now?: () => number;
}

/** One renderer-side owner for release cues. It never creates projectiles,
 * emits damage, changes cast admission, or plays confirmed contact effects. */
export class RemoteProjectileRelease {
  private readonly rows = new Map<string, Entry>();
  private readonly highWater = new Map<string, number>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  constructor(private readonly deps: Dependencies) {}

  observeCast(identity: RemoteReleaseIdentity, fallback?: () => RemoteProjectileReleaseCandidate | null, windUpMs = 0): void {
    if (!this.current(identity)) return;
    this.prune();
    const existing = this.rows.get(identity.castKey);
    if (existing || this.isRetired(identity)) return;
    const actor = this.liveActor(identity.casterId);
    this.remember(identity);
    this.rows.set(identity.castKey, { ...identity, actor: actor?.identity ?? null,
      dueAt: fallback ? this.now() + REMOTE_RELEASE_GRACE_MS : Infinity,
      expiresAt: this.now() + Math.max(0, windUpMs) + REMOTE_RELEASE_TTL_MS,
      settled: !actor, fallback: actor ? fallback : undefined });
    this.bound(); this.start();
  }

  release(candidate: RemoteProjectileReleaseCandidate): void {
    if (!this.current(candidate) || !Number.isFinite(candidate.x) || !Number.isFinite(candidate.y)) return;
    this.prune();
    let row = this.rows.get(candidate.castKey);
    if (!row) {
      if (this.isRetired(candidate)) return;
      const actor = this.liveActor(candidate.casterId);
      row = { ...candidate, actor: actor?.identity ?? null, dueAt: this.now(),
        expiresAt: this.now() + REMOTE_RELEASE_TTL_MS, settled: !actor };
      this.remember(candidate); this.rows.set(candidate.castKey, row); this.bound();
    }
    if (row.casterId !== candidate.casterId || row.abilityId !== candidate.abilityId
      || row.clientCastSeq !== candidate.clientCastSeq || row.settled) return;
    const actor = this.liveActor(candidate.casterId);
    row.settled = true; row.fallback = undefined; // Reentrant plays cannot produce a second cue.
    if (actor?.identity === row.actor) this.deps.emit(candidate);
    this.start();
  }

  cancel(casterId?: string, abilityId?: string, clientCastSeq?: number): void {
    if (arguments.length > 0 && !casterId) return; // Only an explicit no-argument scene cleanup cancels everyone.
    if (casterId && sequence(clientCastSeq)) this.remember({ casterId, abilityId: abilityId ?? '',
      clientCastSeq, castKey: '', scopeKey: this.deps.scope?.() ?? remoteProjectileScopeKey() });
    for (const row of this.rows.values()) if ((!casterId || row.casterId === casterId)
      && (!abilityId || row.abilityId === abilityId)
      && (clientCastSeq === undefined || row.clientCastSeq === clientCastSeq)) {
      row.settled = true; row.fallback = undefined;
    }
    this.start(); // No pending cue survives; one deadline still expires bounded tombstones.
  }

  dispose(): void {
    this.disposed = true; this.stopTimer(); this.rows.clear(); this.highWater.clear();
  }

  private current(identity: RemoteReleaseIdentity): boolean {
    return !this.disposed && identity.scopeKey === (this.deps.scope?.() ?? remoteProjectileScopeKey())
      && identity.castKey.startsWith(identity.scopeKey + ':');
  }
  private liveActor(id: string): RemoteReleaseActor | null {
    const actor = this.deps.caster(id);
    return actor && !actor.isDead && actor.isOnline !== false
      && (actor.health === undefined || (Number.isFinite(actor.health) && actor.health > 0)) ? actor : null;
  }
  private key(identity: RemoteReleaseIdentity): string { return `${identity.scopeKey}:${identity.casterId}`; }
  private isRetired(identity: RemoteReleaseIdentity): boolean {
    return sequence(identity.clientCastSeq) && identity.clientCastSeq <= (this.highWater.get(this.key(identity)) ?? 0);
  }
  private remember(identity: RemoteReleaseIdentity): void {
    if (sequence(identity.clientCastSeq)) this.highWater.set(this.key(identity),
      Math.max(identity.clientCastSeq, this.highWater.get(this.key(identity)) ?? 0));
    while (this.highWater.size > REMOTE_RELEASE_CAP) this.highWater.delete(this.highWater.keys().next().value!);
  }
  private bound(): void { while (this.rows.size > REMOTE_RELEASE_CAP) this.rows.delete(this.rows.keys().next().value!); }
  private prune(): void {
    const now = this.now();
    for (const [key, row] of this.rows) if (row.expiresAt <= now || !this.current(row)) this.rows.delete(key);
  }
  private start(): void {
    this.stopTimer();
    if (this.disposed || !this.rows.size) return;
    let deadline = Infinity;
    for (const row of this.rows.values()) deadline = Math.min(deadline, row.expiresAt, row.settled ? Infinity : row.dueAt);
    if (Number.isFinite(deadline)) this.timer = setTimeout(() => {
      this.timer = null; this.tick();
    }, Math.max(0, deadline - this.now()));
  }
  private stopTimer(): void { if (this.timer) clearTimeout(this.timer); this.timer = null; }
  private tick(): void {
    if (this.disposed) return;
    this.prune();
    for (const row of [...this.rows.values()]) {
      if (this.disposed) break;
      if (this.rows.get(row.castKey) !== row || row.settled || this.now() < row.dueAt) continue;
      const fallback = row.fallback; row.fallback = undefined;
      const candidate = fallback?.();
      if (candidate) this.release(candidate);
      row.settled = true;
    }
    this.start();
  }
  private now(): number { return this.deps.now?.() ?? Date.now(); }
}
